/**
 * Bridge OrderDesk "Wholesale (Sent to Xero)" orders back into a selected
 * dated Packers / Production folder view when dispatch/delivery still belong
 * to that day or week.
 */

import { formatYYYYMMDD, getStartOfWeek, parseDDMMYYYY } from './dateService.js';
import { getOrderDeliveryDate, getOrderDispatchDate, normalizeOrderDate } from '../utils/orderDateUtils.js';
import {
  fetchOrderDeskFolderOrders,
  isOrderDeskDateFolderName,
} from './orderDeskFolderOrdersService.js';

export const XERO_WHOLESALE_FOLDER_NAME = 'Wholesale (Sent to Xero)';

const CACHE_TTL_MS = 60_000;
let xeroOrdersCache = { key: '', at: 0, orders: null };

export function findXeroWholesaleFolder(folders = []) {
  const exact = (folders || []).find(
    (folder) => String(folder?.name || '').trim() === XERO_WHOLESALE_FOLDER_NAME,
  );
  if (exact) return exact;
  return (
    (folders || []).find((folder) =>
      /wholesale\s*\(?\s*sent\s*to\s*xero\s*\)?/i.test(String(folder?.name || '')),
    ) || null
  );
}

/**
 * Friday-named folders are treated as Mon–Fri week folders.
 * Other weekday folders match that single calendar day (busy-period day folders).
 * @returns {{ start: string, end: string, folderIso: string, mode: 'week'|'day' }|null}
 */
export function getSelectedFolderMatchBounds(folderName) {
  const name = String(folderName || '').trim();
  if (!isOrderDeskDateFolderName(name)) return null;
  const folderDate = parseDDMMYYYY(name);
  if (!folderDate || Number.isNaN(folderDate.getTime())) return null;
  folderDate.setHours(12, 0, 0, 0);
  const folderIso = formatYYYYMMDD(folderDate);
  if (folderDate.getDay() === 5) {
    const mondayIso = getStartOfWeek(folderIso);
    if (!mondayIso) return null;
    return { start: mondayIso, end: folderIso, folderIso, mode: 'week' };
  }
  return { start: folderIso, end: folderIso, folderIso, mode: 'day' };
}

export function normalizeFlexibleOrderDate(value) {
  if (value == null || value === '') return null;
  const asString = String(value).trim();
  if (!asString) return null;
  const fromHelper = normalizeOrderDate(asString);
  if (fromHelper) return fromHelper;
  const parsed = parseDDMMYYYY(asString);
  if (parsed) return formatYYYYMMDD(parsed);
  if (/^\d{4}-\d{2}-\d{2}/.test(asString)) return asString.slice(0, 10);
  const loose = new Date(asString);
  if (!Number.isNaN(loose.getTime())) return formatYYYYMMDD(loose);
  return null;
}

const isoInInclusiveRange = (iso, start, end) =>
  Boolean(iso && start && end && iso >= start && iso <= end);

/**
 * Prefer delivery AND dispatch inside bounds; if not both, fall back to delivery.
 * @returns {{ include: boolean, quality: 'both'|'delivery'|null, deliveryIso: string|null, dispatchIso: string|null }}
 */
export function classifyXeroOrderForFolder(order, bounds) {
  if (!bounds?.start || !bounds?.end) {
    return { include: false, quality: null, deliveryIso: null, dispatchIso: null };
  }
  const deliveryIso = normalizeFlexibleOrderDate(
    getOrderDeliveryDate(order) || order?.deliveryDate || order?.delivery_date,
  );
  const dispatchIso = normalizeFlexibleOrderDate(
    getOrderDispatchDate(order) || order?.dispatchDate || order?.dispatch_date,
  );
  const deliveryMatch = isoInInclusiveRange(deliveryIso, bounds.start, bounds.end);
  const dispatchMatch = isoInInclusiveRange(dispatchIso, bounds.start, bounds.end);

  if (deliveryMatch && dispatchMatch) {
    return { include: true, quality: 'both', deliveryIso, dispatchIso };
  }
  if (deliveryMatch) {
    return { include: true, quality: 'delivery', deliveryIso, dispatchIso };
  }
  return { include: false, quality: null, deliveryIso, dispatchIso };
}

export function getOrderBridgeKeys(order) {
  return Array.from(
    new Set(
      [
        order?.source_id,
        order?.sourceId,
        order?.orderNumber,
        order?.orderId,
        order?.order_id,
        order?.id,
      ]
        .map((value) => String(value || '').replace(/^#/, '').trim())
        .filter(Boolean),
    ),
  );
}

/**
 * Re-home a Xero-folder order onto the selected Packers folder for list/requirements
 * while preserving the real OD folder for the badge.
 *
 * IDENTITY INVARIANT: This must not invent a new order. Packed / shipped status and
 * requirement exclusion are keyed to source_id / OrderDesk id variants in Firestore
 * (`packedOrders` / `shippedOrders`). Folder moves (including into Wholesale
 * (Sent to Xero)) must not clear, reopen, or double-count that status — the same
 * order keeps the same effect on numbers when linked back into a dated folder view.
 */
export function adaptXeroOrderForSelectedFolder(order, selectedFolder, match) {
  const sourceId = String(
    order?.source_id || order?.sourceId || order?.orderNumber || order?.orderId || '',
  ).replace(/^#/, '').trim();
  const orderDeskId = order?.order_id || order?.id || order?.orderId || null;
  return {
    ...order,
    orderId: order?.orderId || sourceId || orderDeskId,
    orderNumber: sourceId || order?.orderNumber,
    sourceId: sourceId || order?.sourceId,
    source_id: sourceId || order?.source_id,
    // Keep every identity the packer may have written to packedOrders / shippedOrders.
    id: order?.id || orderDeskId || sourceId,
    order_id: order?.order_id || orderDeskId || null,
    order_number: order?.order_number || order?.orderNumber || sourceId || null,
    folderId: selectedFolder?.id ?? null,
    folderName: selectedFolder?.name ?? null,
    odFolderId: order?.folderId ?? null,
    odFolderName: order?.folderName || XERO_WHOLESALE_FOLDER_NAME,
    sentToXero: true,
    linkedFromXeroFolder: true,
    xeroMatchQuality: match?.quality || 'delivery',
  };
}

async function loadXeroFolderOrders(xeroFolder) {
  const key = String(xeroFolder?.id ?? xeroFolder?.name ?? '');
  if (
    xeroOrdersCache.key === key &&
    Date.now() - xeroOrdersCache.at < CACHE_TTL_MS &&
    Array.isArray(xeroOrdersCache.orders)
  ) {
    return xeroOrdersCache.orders;
  }
  const orders = await fetchOrderDeskFolderOrders({
    folder: xeroFolder,
    dateField: 'delivery',
  });
  xeroOrdersCache = { key, at: Date.now(), orders };
  return orders;
}

/**
 * @returns {Promise<{ linkedOrders: object[], xeroFolder: object|null, bounds: object|null, skippedReason?: string }>}
 */
export async function fetchXeroLinkedOrdersForSelectedFolder({
  selectedFolder,
  folders = [],
} = {}) {
  const bounds = getSelectedFolderMatchBounds(selectedFolder?.name);
  if (!bounds) {
    return {
      linkedOrders: [],
      xeroFolder: null,
      bounds: null,
      skippedReason: 'Selected folder is not a dated OrderDesk folder.',
    };
  }
  const xeroFolder = findXeroWholesaleFolder(folders);
  if (!xeroFolder) {
    return {
      linkedOrders: [],
      xeroFolder: null,
      bounds,
      skippedReason: `Folder "${XERO_WHOLESALE_FOLDER_NAME}" was not found.`,
    };
  }

  const xeroOrders = await loadXeroFolderOrders(xeroFolder);
  const linkedOrders = [];
  for (const order of xeroOrders || []) {
    const match = classifyXeroOrderForFolder(order, bounds);
    if (!match.include) continue;
    linkedOrders.push(adaptXeroOrderForSelectedFolder(order, selectedFolder, match));
  }
  return { linkedOrders, xeroFolder, bounds };
}

/** @visibleForTesting */
export function __resetXeroWholesaleFolderBridgeCacheForTests() {
  xeroOrdersCache = { key: '', at: 0, orders: null };
}
